// hooks/useVoice.js
//
// Drop-in replacement for the local `useVoice` defined inside CareCopilot.js
// and CareCopilotWidget.js. Same return shape as the widget's version:
//   { speak, stopSpeaking, startListening, stopListening, speaking, listening, supported }
//
// TTS (speak/stopSpeaking) is unchanged — window.speechSynthesis works fine in
// both iOS WKWebView and Android's system WebView.
//
// STT (startListening/stopListening) now uses the native Capacitor plugin
// (@capacitor-community/speech-recognition) instead of window.webkitSpeechRecognition,
// which is NOT implemented in iOS WKWebView and is unreliable on Android WebView.
// Falls back to the Web Speech API only when running in a plain desktop/mobile
// browser tab (not the native app) — useful for local dev without a device build.
//
// Setup required (see chat for full steps):
//   npm install @capacitor-community/speech-recognition
//   npx cap sync
//   iOS Info.plist: NSSpeechRecognitionUsageDescription, NSMicrophoneUsageDescription
//   Android Manifest: <uses-permission android:name="android.permission.RECORD_AUDIO" />

import { useState, useEffect, useRef, useCallback } from 'react';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';
import { Capacitor } from '@capacitor/core';

function stripMarkdown(text) {
  return (text || '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/<br>/g, ' ')
    .replace(/→/g, '')
    .replace(/✅/g, '')
    .replace(/\n/g, ' ')
    .trim();
}

export function useVoice({ muted, onTranscript } = {}) {
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState({ tts: false, stt: false });

  const isNative = Capacitor.isNativePlatform();
  const latestTranscriptRef = useRef('');
  const partialListenerRef = useRef(null);
  const webRecognitionRef = useRef(null);

  // ── Feature detection on mount ─────────────────────────────────────────────
  useEffect(() => {
    const ttsSupported = 'speechSynthesis' in window;

    if (isNative) {
      SpeechRecognition.available()
        .then(({ available }) => setSupported({ tts: ttsSupported, stt: available }))
        .catch(() => setSupported({ tts: ttsSupported, stt: false }));
    } else {
      // Desktop/browser dev fallback only — not used in the shipped app
      const webSTT = 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;
      setSupported({ tts: ttsSupported, stt: webSTT });
    }
  }, [isNative]);

  // ── Text to speech ──────────────────────────────────────────────────────────
  const speak = useCallback((text) => {
    if (muted || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(stripMarkdown(text));
    utt.rate = 0.95;
    utt.pitch = 1;
    utt.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferred =
      voices.find(
        (v) => v.lang.startsWith('en') && (v.name.includes('Natural') || v.name.includes('Samantha') || v.name.includes('Google'))
      ) || voices.find((v) => v.lang.startsWith('en'));
    if (preferred) utt.voice = preferred;
    utt.onstart = () => setSpeaking(true);
    utt.onend = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
  }, [muted]);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  }, []);

  // ── Speech to text — native (iOS/Android via Capacitor plugin) ──────────────
  const startListeningNative = useCallback(async () => {
    try {
      const { available } = await SpeechRecognition.available();
      if (!available) return;

      const perm = await SpeechRecognition.requestPermissions();
      const granted = perm.speechRecognition === 'granted' || perm.speechRecognition === 'limited';
      if (!granted) {
        setSupported((s) => ({ ...s, stt: false }));
        return;
      }

      latestTranscriptRef.current = '';

      partialListenerRef.current = await SpeechRecognition.addListener('partialResults', (data) => {
        if (data?.matches?.length) {
          latestTranscriptRef.current = data.matches[0];
        }
      });

      await SpeechRecognition.start({
        language: 'en-US',
        maxResults: 1,
        partialResults: true,
        popup: false,
      });

      setListening(true);
    } catch (err) {
      setListening(false);
    }
  }, []);

  const stopListeningNative = useCallback(async () => {
    try {
      await SpeechRecognition.stop();
    } catch (err) {
      // already stopped — ignore
    }
    if (partialListenerRef.current) {
      partialListenerRef.current.remove();
      partialListenerRef.current = null;
    }
    setListening(false);

    const transcript = latestTranscriptRef.current;
    latestTranscriptRef.current = '';
    if (transcript && onTranscript) {
      onTranscript(transcript);
    }
  }, [onTranscript]);

  // ── Speech to text — web fallback (dev/browser only) ────────────────────────
  const startListeningWeb = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const recognition = new SR();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onstart = () => setListening(true);
    recognition.onresult = (e) => onTranscript?.(e.results[0][0].transcript);
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    webRecognitionRef.current = recognition;
    recognition.start();
  }, [onTranscript]);

  const stopListeningWeb = useCallback(() => {
    webRecognitionRef.current?.stop();
    setListening(false);
  }, []);

  // ── Unified interface ────────────────────────────────────────────────────────
  const startListening = useCallback(() => {
    if (listening) return;
    if (isNative) startListeningNative();
    else startListeningWeb();
  }, [isNative, listening, startListeningNative, startListeningWeb]);

  const stopListening = useCallback(() => {
    if (isNative) stopListeningNative();
    else stopListeningWeb();
  }, [isNative, stopListeningNative, stopListeningWeb]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (partialListenerRef.current) partialListenerRef.current.remove();
      if (isNative) SpeechRecognition.stop().catch(() => {});
      webRecognitionRef.current?.stop();
    };
  }, [isNative]);

  return { speak, stopSpeaking, startListening, stopListening, speaking, listening, supported };
}

export default useVoice;